import { ValidationConfig } from "@/constants/validation-config";
import * as lodash from "lodash";
import dayjs from "dayjs";
import { _$t } from "@/utils/i18n";

// 検証スキーマ
export interface ValidationSchema {
  // 必須項目
  required: any;
  // 型
  type: any;
  // プロパティ
  properties: any;
}

// 返却用インターフェース
export interface ValidationResult {
  valid: boolean;
  errors: { msg: string }[];
}

// 返却用インターフェース
export interface ValidationError {
  key: string;
  value: string;
  validation_type: string;
  msg: string;
}

export const validationCommon = {
  // 検証スキーマ作成
  createSchema(obj: any): ValidationSchema {
    const validationConfig = new ValidationConfig();
    const required: any = [];
    const properties: any = lodash.cloneDeep(obj);

    for (const key in properties) {
      //必須チェック項目を作成
      if (properties[key]["required"]) required.push(key);
      //必須チェック項目は除外する
      delete properties[key]["required"];

      //独自チェック項目が含まれている場合は除外する
      if (properties[key]["addOn"] !== undefined)
        delete properties[key]["addOn"];

      // パターン名からを正規表現を取得
      if (properties[key]["pattern"] !== undefined) {
        const pattern: any = properties[key]["pattern"];
        properties[key]["pattern"] = validationConfig.patterns[pattern];
      }
    }

    const schema = {
      required: required,
      type: "object",
      properties: properties,
    };
    return schema;
  },

  // エラー情報の作成
  convertAjvError(obj: any, model: any): ValidationError {
    const validationConfig = new ValidationConfig();
    // ajvの仕様上、必須チェックとその他のチェックでプロパティ名の取得方法を分ける必要がある
    const key =
      obj.keyword === "required"
        ? obj.params.missingProperty
        : obj.instancePath.slice(1);
    const value = model.attributes[key];
    const validation_type = obj.keyword;
    let msg;
    if (validation_type === "pattern") {
      const pattern = model.validation[key]["pattern"];
      msg = validationCommon.format(
        validationConfig.messages[validation_type],
        model.labels[key],
        validationConfig.patternNames[pattern]
      );
    } else {
      msg = validationCommon.format(
        validationConfig.messages[validation_type],
        model.labels[key],
        model.validation[key][validation_type]
      );
    }

    return {
      key: key,
      value: value,
      validation_type: validation_type,
      msg: msg,
    };
  },

  // 返却情報の作成
  createResult(valid: boolean, errors: any): ValidationResult {
    return {
      valid: valid,
      errors: errors,
    };
  },

  // エラー返却（keyに対して一番最初のエラーのみ抽出）
  createUniqKeyErrors(errors: any): any {
    const reducer = (previous: any, current: any) => {
      if (!previous.some((e: ValidationError) => e.key === current.key))
        previous.push(current);
      return previous;
    };
    return errors.reduce(reducer, []);
  },

  // 独自チェックを持つ項目リストの作成
  getAddonList(validation: any): any {
    const list = [];
    for (const key in validation) {
      if (validation[key]["addOn"] !== undefined)
        list.push({ key: key, value: validation[key] });
    }
    return list;
  },

  /**
   * 値のtrimを行う。
   * hasValueで、使用している。
   * @method trim
   * @param {String} value トリムしたい文字列。
   * @return {String} トリムした文字列。 引数がnullの場合は、空文字。
   */
  trim: function (value: string) {
    const trimLeft = /^\s+/;
    const trimRight = /\s+$/;
    if (value === null) {
      return "";
    } else {
      return value.replace(trimLeft, "").replace(trimRight, "");
    }
  },

  /**
   * 値の存在チェックを行う。
   * pattern用に、使用する。
   * 独自で作成したvalidatorにも、使用すること。
   * この中で、使用しているtrimは、別途定義している。
   * @method hasValue
   * @param {String} value 値の存在チェックをしたい文字列。
   * @return {Boolean} true:値が存在する、false:値が存在しない。
   */
  hasValue(value: string): boolean {
    // return !(_.isNull(value) || _.isUndefined(value) || (_.isString(value) && ValidationCommon.prototype.trim(value) === ''));
    return !(
      value === null ||
      value === undefined ||
      (typeof value === "string" && validationCommon.trim(value) === "")
    );
  },

  //// *
  // メッセージのフォーマット処理を行う。
  // backbone-validation.jsから移植した。
  // pattern用に、使用する。
  // 独自で作成したvalidatorにも、使用すること。
  // @method format
  // @param {String[[]]} arguments 要素１：メッセージ書式、要素２：項目名、要素３：メッセージ内容
  // @return {String} フォーマットしたメッセージ文字列。
  // @example
  //      validationCommon.format(validationConfig.messages.noFullHyphen, model.labels[attr], model.labels[customValue])
  ////
  format: function (...args: any) {
    const text = args.shift();
    return text.replace(/\{(\d+)\}/g, function (match: any, number: any) {
      if (args[number]?.toString()) {
        return args[number];
      } else {
        return match;
      }
    });
  },

  // 独自チェック用関数
  // 必要あれば足していく
  addOnFunc: {
    // 半角スペースのチェック
    // 前後にある場合もエラー対象。
    noHalfSpace: (value: any, attr: any, customValue: boolean, model: any) => {
      let error: null | ValidationError = null;
      const validationConfig = new ValidationConfig();
      // 値がない場合は、チェックしない。
      if (!validationCommon.hasValue(value)) {
        return "";
      }
      let msg = "";
      // noHalfSpaceに、trueが設定されている場合
      if (customValue === true) {
        // 半角スペースが含まれている場合
        if (!(value.indexOf(" ") === -1)) {
          msg = validationCommon.format(
            validationConfig.messages.noHalfSpace,
            model.labels[attr]
          );
        }
      }

      if (msg) {
        error = {
          key: attr,
          value: value,
          validation_type: "noHalfSpace",
          msg: msg,
        };
      }

      return error;
    },
    // 値が含まれているかチェック
    oneOf: (value: any, attr: any, customValue: any, model: any) => {
      let error: null | ValidationError = null;
      const validationConfig = new ValidationConfig();
      // 値がない場合は、チェックしない。
      if (!validationCommon.hasValue(value)) {
        return "";
      }
      let msg = "";
      let result = false;
      if (customValue && customValue.length > 0) {
        for (const item of customValue) {
          if (value === item) {
            result = true;
          }
        }
      }
      if (!result) {
        msg = validationCommon.format(
          validationConfig.messages.oneOf,
          model.labels[attr]
        );
      }

      if (msg) {
        error = {
          key: attr,
          value: value,
          validation_type: "oneOf",
          msg: msg,
        };
      }

      return error;
    },
    // 値が範囲内であるかチェック
    range: (value: any, attr: any, customValue: any, model: any) => {
      let error: null | ValidationError = null;
      const validationConfig = new ValidationConfig();
      // 値がない場合は、チェックしない。
      if (!validationCommon.hasValue(value)) {
        return "";
      }
      let msg = "";
      let result = false;
      if (customValue && customValue.length === 2) {
        if (value >= customValue[0] && value <= customValue[1]) {
          result = true;
        }
      }
      if (!result) {
        msg = validationCommon.format(
          validationConfig.messages.range,
          model.labels[attr],
          customValue[0],
          customValue[1]
        );
      }

      if (msg) {
        error = {
          key: attr,
          value: value,
          validation_type: "range",
          msg: msg,
        };
      }

      return error;
    },
    // 禁則文字チェック
    // 文字列が完全一致する場合はNG、前方と後方一致は対象外
    ngword: (value: any, attr: any, customValue: any, model: any) => {
      let error: null | ValidationError = null;
      const validationConfig = new ValidationConfig();
      // 値がない場合は、チェックしない。
      if (!validationCommon.hasValue(value)) return "";
      let msg = "";
      //
      if (customValue && customValue.length > 0) {
        if (customValue.includes(value)) {
          msg = validationCommon.format(
            validationConfig.messages.ngword,
            model.labels[attr]
          );
        }
      }

      if (msg) {
        error = {
          key: attr,
          value: value,
          validation_type: "ngword",
          msg: msg,
        };
      }

      return error;
    },

    // 指定のフィールドと値が一致しているかチェック
    // もともとbackboneで実装されていたものをここに実装
    equalTo: (value: any, attr: any, customValue: string, model: any) => {
      let error: null | ValidationError = null;
      const validationConfig = new ValidationConfig();

      const targetField = customValue;
      // 比較対象の値を取得する。
      const targetValue = model.attributes[targetField];
      let msg = "";

      // if (!validationCommon.hasValue(value))
      //   return ""

      // 比較対象のフィールドの値が、指定の値の場合
      // このフィールドの必須チェックを行う。
      if (value === targetValue) return "";
      else
        msg = validationCommon.format(
          validationConfig.messages.equalTo,
          model.labels[attr],
          model.labels[customValue]
        );

      if (msg)
        error = {
          key: attr,
          value: value,
          validation_type: "equalTo",
          msg: msg,
        };

      return error;
    },

    // 全角ハイフン『ー』のチェック
    noFullHyphen: (value: any, attr: any, customValue: boolean, model: any) => {
      let error: null | ValidationError = null;
      const validationConfig = new ValidationConfig();
      // 値がない場合は、チェックしない。
      if (!validationCommon.hasValue(value)) return "";

      let msg = "";
      //
      if (customValue === true)
        if (!(value.indexOf("ー") === -1))
          // noFullHyphenに、trueが設定されている場合
          // 全角ハイフン『ー』が含まれている場合
          msg = validationCommon.format(
            validationConfig.messages.noFullHyphen,
            model.labels[attr]
          );

      if (msg)
        error = {
          key: attr,
          value: value,
          validation_type: "noFullHyphen",
          msg: msg,
        };

      return error;
    },

    // 全角スペースのチェック
    noFullSpace: (value: any, attr: any, customValue: boolean, model: any) => {
      let error: null | ValidationError = null;
      const validationConfig = new ValidationConfig();
      // 値がない場合は、チェックしない。
      if (!validationCommon.hasValue(value)) return "";

      let msg = "";
      //
      if (customValue === true)
        if (!(value.indexOf("　") === -1))
          // noFullSpaceに、trueが設定されている場合
          // 全角ハイフン『ー』が含まれている場合
          msg = validationCommon.format(
            validationConfig.messages.noFullSpace,
            model.labels[attr]
          );

      if (msg)
        error = {
          key: attr,
          value: value,
          validation_type: "noFullSpace",
          msg: msg,
        };

      return error;
    },

    // 必須の相関チェック（Q2で未使用だったので、コメントアウトで記載）
    // ターゲット項目が期待値と同値の場合に、必須チェックを行う。
    // ターゲット項目は、モデルに格納されている必要がある。
    // requiredIf: (value: any, attr: any, customValue: any, model: any) => {

    //   let error: null | ValidationError = null;
    //   const validationConfig = new ValidationConfig

    //   const targetField = customValue[0]
    //   const expectedValue = customValue[1]
    //   // 比較対象の値を取得する。
    //   const targetValue = model[targetField]

    //   let msg = ""
    //   // 比較対象のフィールドの値が、指定の値の場合
    //   // このフィールドの必須チェックを行う。
    //   if (expectedValue === targetValue) {
    //     if (validationCommon.hasValue(value)) {
    //       return ""
    //     } else {
    //       msg = validationCommon.format(validationConfig.messages.requiredIf, model.labels[attr], model.labels[customValue])
    //     }
    //   } else {
    //     return ""
    //   }

    //   if (msg){
    //     error = {
    //       key: attr,
    //       value: value,
    //       validation_type: "requiredIf",
    //       msg: msg
    //     }
    //   }

    //   return error;
    // },

    // 日付の相関チェック（Q2で未使用だったので、コメントアウトで記載）
    // value > targetDateは、OK。
    // customValueに指定された項目の日付より、後の日であるかを判定する。
    // yyyy/mm/dd形式のチェックが事前に行われていること。
    // dateAfter: (value: any, attr: any, customValue: string, model: any) => {
    //   let error: null | ValidationError = null;
    //   const validationConfig = new ValidationConfig
    //   const customValueDate = model.attributes[customValue]
    //   // 値がない場合は、チェックしない。
    //   if (!(validationCommon.hasValue(value)) || !(validationCommon.hasValue(customValueDate))){
    //     return ""
    //   }
    //   let msg = ""
    //   let valueDate = value.replace(/\//g, "")
    //   // 比較対象の値を取得する。
    //   let targetDate = customValueDate.replace(/\//g, "")
    //   // 日付を比較する。
    //   if(valueDate <= targetDate){
    //     msg = validationCommon.format(validationConfig.messages.dateAfter, model.labels[attr], model.labels[customValue])
    //   }

    //   if (msg){
    //     error = {
    //       key: attr,
    //       value: value,
    //       validation_type: "dateAfter",
    //       msg: msg
    //     }
    //   }

    //   return error;
    // },

    // 日付の相関チェック
    // value >= targetDateは、OK。
    // customValueに指定された項目の日付以降の日であるかを判定する。
    // yyyy/mm/dd形式のチェックが事前に行われていること。
    dateAfterEqual: (
      value: any,
      attr: any,
      customValue: string,
      model: any
    ) => {
      let error: null | ValidationError = null;
      const validationConfig = new ValidationConfig();
      const customValueDate = model.attributes[customValue];
      // 値がない場合は、チェックしない。
      if (
        !validationCommon.hasValue(value) ||
        !validationCommon.hasValue(customValueDate)
      )
        return "";
      let msg = "";
      const valueDate = value.replace(/\//g, "");
      // 比較対象の値を取得する。
      const targetDate = customValueDate.replace(/\//g, "");
      // 日付を比較する。
      if (valueDate < targetDate)
        msg = validationCommon.format(
          validationConfig.messages.dateAfterEqual,
          model.labels[attr],
          model.labels[customValue]
        );

      if (msg)
        error = {
          key: attr,
          value: value,
          validation_type: "dateAfterEqual",
          msg: msg,
        };

      return error;
    },

    // 日付の期間チェック
    // 終了日に、条件として記述する。
    // customValueに指定された項目（開始日、期間種類（年、月、日）、期間）の期間であるかを判定する。
    // yyyy/mm/dd形式のチェックが事前に行われていること。
    datePeriod: (
      value: any,
      attr: any,
      customValue: [string, dayjs.UnitType, number],
      model: any
    ) => {
      let error: null | ValidationError = null;
      const validationConfig = new ValidationConfig();

      const targetField = customValue[0]; // 例）startDate
      const periodType = customValue[1]; // 例）years、months、days
      const periodNumber = customValue[2]; // 例）2
      // 比較対象の値（期間開始日）を取得する。
      const customValueDate = model.attributes[targetField];

      // 値がない場合は、チェックしない。
      if (
        !validationCommon.hasValue(value) ||
        !validationCommon.hasValue(customValueDate)
      )
        return "";

      const startDateMoment = dayjs(customValueDate);
      const endDateMoment = dayjs(value);

      // 期間を算出する。
      // periodTypeの値は、momentの仕様に沿う。
      const diffPeriods = endDateMoment.diff(startDateMoment, periodType) + 1;
      let periodNumberText;
      if (periodType === "years") {
        periodNumberText =
          periodNumber + _$t("msg_common_unit_period_years_label");
      } else if (periodType === "months") {
        periodNumberText =
          periodNumber + _$t("msg_common_unit_period_months_label");
      } else if (periodType === "days") {
        periodNumberText =
          periodNumber + _$t("msg_common_unit_period_days_label");
      }

      let msg = "";
      // 期間を比較する。
      if (diffPeriods > periodNumber)
        msg = validationCommon.format(
          validationConfig.messages.datePeriod,
          model.labels[attr],
          model.labels[targetField],
          periodNumberText
        );

      if (msg)
        error = {
          key: attr,
          value: value,
          validation_type: "datePeriod",
          msg: msg,
        };

      return error;
    },

    // YYYYMMDDHHmm形式の日時相関チェック
    // value = targetDateTime は NG。
    dateAndTimeAfter: (
      value: any,
      attr: any,
      customValue: string,
      model: any
    ) => {
      let error: null | ValidationError = null;
      const validationConfig = new ValidationConfig();
      const customValueDateTime = model.attributes[customValue];

      // 値がない場合は、チェックしない。
      if (
        !validationCommon.hasValue(value) ||
        !validationCommon.hasValue(customValueDateTime)
      ) {
        return "";
      }

      let msg = "";
      // 数字以外を削除
      const valueDatetime = value.replace(/[^0-9]/g, "");
      const targetDatetime = customValueDateTime.replace(/[^0-9]/g, "");

      // 日付と時間を比較する。
      if (valueDatetime < targetDatetime || valueDatetime === targetDatetime) {
        msg = validationCommon.format(
          validationConfig.messages.dateAndTimeAfter,
          model.labels[attr],
          model.labels[customValue]
        );
      }

      if (msg) {
        error = {
          key: attr,
          value: value,
          validation_type: "dateAndTimeAfter",
          msg: msg,
        };
      }

      return error;
    },
  },
};
